/**
 * External Imports
 */
import { connect } from 'react-redux';
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useRef,
  useState
} from 'react';

/**
 * Nexus Imports
 */
import { NeuButton, NeuInput } from '@neutron/react';

/**
 * Internal Imports
 */
import { Dropdown } from '../../../../components/shared/dropdowns';

import { usePostMasterTemplateMutation } from '../../../../redux/apis/Templates.api';

import { formatRoundingType } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  ICondensedMasterTemplate,
  IFullTemplate
} from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  InputHeading,
  NewTemplatesTreeBtnContainer,
  StyledTemplateBtn
} from './NewMasterTemplate.styles';

interface INewMasterTemplateProps {
  menuItemsMinusLabels: { tabId: string; roundingTypes: string[] }[];
  selectedTab: string;
  templateTypes: string[];
  userId: string;
  setSelectedMasterTemplate: Dispatch<
    SetStateAction<ICondensedMasterTemplate | IFullTemplate | null>
  >;
  setSelectedTab: Dispatch<SetStateAction<string>>;
  setShowNewMasterTemplate: (value: boolean) => void;
}

const { v1: uuidv1 } = require('uuid');

const NewMasterTemplate: FC<INewMasterTemplateProps> = ({
  menuItemsMinusLabels,
  selectedTab,
  templateTypes,
  userId,
  setSelectedMasterTemplate,
  setSelectedTab,
  setShowNewMasterTemplate
}) => {
  const [selectedTemplateType, setSelectedTemplateType] = useState<string>(
    menuItemsMinusLabels.find(item => item.tabId === selectedTab)
      ?.roundingTypes[0] || 'patient'
  );
  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  const [open, setOpen] = useState(false);

  const templateName = useRef<string>('');

  const [
    postMasterTemplate
    // {
    //   isError: isPostMasterTemplateError,
    //   isLoading: isPostMasterTemplateLoading,
    //   isSuccess: isPostMasterTemplateSuccess
    // }
  ] = usePostMasterTemplateMutation();

  const handleDropdownChange = useCallback((value: string, name: string) => {
    switch (name) {
      case 'templateType':
        setSelectedTemplateType(value);
        break;

      default: {
        console.log('error');
      }
    }
  }, []);

  const handleInputs = (e: any) => {
    const { name } = e.target;
    const { value } = e.target;
    switch (name) {
      case 'templateName':
        templateName.current = value;
        break;

      default: {
        console.log('error');
      }
    }
    if (templateName.current?.length !== 0) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  };

  const handleNewMasterTemplate = async () => {
    const newTemplateId = uuidv1();

    const payload: any = {
      conditions: [],
      configuration: { questions: {} },
      name: templateName.current,
      questions: [],
      roundingType:
        selectedTemplateType === 'csc' ? 'csrn' : selectedTemplateType,
      templateConfigId: newTemplateId,
      templateId: newTemplateId,
      templateLevel: 'master',
      templateLevelId: 'HCA'
    };

    try {
      await postMasterTemplate({
        ...payload,
        userId
      });
      // appInsights.trackEvent({
      //   name: 'Master_Template_Created'
      // });
      setSelectedMasterTemplate(payload);
      const validSelectedTab =
        menuItemsMinusLabels.find(item =>
          item.roundingTypes.includes(
            payload.roundingType === 'csrn' ? 'csc' : payload.roundingType
          )
        )?.tabId || 'patient';
      if (validSelectedTab !== selectedTab) setSelectedTab(validSelectedTab);
      setTimeout(() => setShowNewMasterTemplate(false), 500);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div
      className="d-flex flex-column position-absolute"
      style={{ maxWidth: '26em' }}
    >
      <div className="d-flex flex-column">
        <InputHeading>Template Type</InputHeading>
        <Dropdown
          classes="ps-0"
          name="templateType"
          open={open}
          placeholder={formatRoundingType(selectedTemplateType)}
          setOpen={setOpen}
          styles={{ paddingTop: '7px' }}
        >
          {templateTypes.map((template: any = {}) => (
            <div
              key={template}
              aria-selected={template === selectedTemplateType}
              className={`dropdown-hover-item${
                template === selectedTemplateType ? ' selected' : ''
              }`}
              role="option"
              tabIndex={0}
              onClick={() => handleDropdownChange(template, 'templateType')}
              onKeyDown={() => handleDropdownChange(template, 'templateType')}
            >
              {formatRoundingType(template)}
            </div>
          ))}
        </Dropdown>
      </div>
      <div className="d-flex flex-column mt-1">
        <NeuInput
          id="New-Master-Template-Name-Input"
          label="Template Name"
          name="templateName"
          autocomplete="off"
          inputMode="text"
          autoCorrect="off"
          required
          spellCheck
          onNeuChange={handleInputs}
        />
      </div>
      <NewTemplatesTreeBtnContainer className="d-flex justify-content-between align-items-center mt-2">
        <StyledTemplateBtn
          data-testid="cancel-template-button"
          class="me-3"
          color="blue"
          kind="secondary"
          size="small"
          style={{ marginLeft: 'auto' }}
          onClick={() => setShowNewMasterTemplate(false)}
        >
          Cancel
        </StyledTemplateBtn>
        <NeuButton
          data-testid="create-template-button"
          color="blue"
          disabled={isDisabled}
          kind="primary"
          size="small"
          onClick={!isDisabled ? handleNewMasterTemplate : undefined}
        >
          Create Template
        </NeuButton>
      </NewTemplatesTreeBtnContainer>
    </div>
  );
};

const mapReduxStateToProps = (state: any) => ({
  userId: state.AuthorizedUser.authorizedUser.hcaid
});

export default connect(mapReduxStateToProps)(NewMasterTemplate);
